<template>
  <div class="wrap wrap_card">
    <div @click="toggleCollapse" class="manager-totals-grid cursor-pointer" :class="$can(permissions.ReadAllComments) ? 'with-comments' : 'without-comments'">
      <!-- Manager name spans multiple columns -->
      <div class="total-cell manager-name-cell" :class="$can(permissions.ReadAllComments) ? 'manager-name-with-comments' : 'manager-name-without-comments'">
        <p class="manager-name">{{ data.managerName }}</p>
      </div>

      <!-- Show totals when collapsed, empty cells when expanded -->
      <template v-if="!isCollapsed">
        <div class="total-cell total-employee">
          <span class="manager-totals-label">TOTAL</span>
        </div> <!-- Employee: 15.8% -->
        <div class="total-cell total-planned">
          <span class="manager-totals-amount">{{ getTotalPlanned() }}</span>
        </div> <!-- Planned $: 7.6% -->
        <div class="total-cell total-paid">
          <span class="manager-totals-amount" :class="getTotalPaidAmountClass()"
                v-b-tooltip.hover.html="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title="getTotalPaidByTooltipHtml()">{{ getTotalPaid() }}</span>
        </div> <!-- Paid $: 7.6% -->
        <div class="total-cell total-commission"></div> <!-- Commission: 7.6% -->
      </template>
      <template v-else>
        <div class="total-cell"></div> <!-- Empty Employee -->
        <div class="total-cell"></div> <!-- Empty Planned $ -->
        <div class="total-cell"></div> <!-- Empty Paid $ -->
        <div class="total-cell"></div> <!-- Empty Commission -->
      </template>

      <!-- Arrow icon in last column -->
      <div class="total-cell total-arrow">
        <span class="material-symbols-outlined arrow-icon" style="font-size: 20px">
          {{ isCollapsed ? 'keyboard_arrow_up' :  'keyboard_arrow_down'}}
        </span>
      </div> <!-- Invoices: 7.6% -->
    </div>
    <b-collapse v-model="isCollapsed">
      <div class="divider"></div>
      <!-- <code>{{ data.projects[0] }}</code> -->
      <div class="position-relative table-wrapper">
        <b-table-simple table-class="table-fixed" class="table__theme-default table-sales-report" :hover="false" stripped responsive>
          <b-thead>
            <b-tr class="thead-row row-top">

              <b-th v-for="field in currentFields" :key="field.key" :class="field.class" :style="{
                'max-width': field.width ? field.width + 'px' : '',
                'min-width': field.width ? field.width + 'px' : '',
                width: field.width ? field.width + 'px' : '',
              }">{{ field.label }}</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr v-for="(rowData, i) in flattenedProjects" :key="`${data.managerName}-${i}-${rowData.isEmployeeRow ? 'employee' : 'project'}`" :class="{ 'employee-detail-row': rowData.isEmployeeRow }">
              <!-- Project Row Content -->
              <template v-if="!rowData.isEmployeeRow">
                <b-td>
                  <div class="d-flex align-items-center text-left">
                    <button
                      class="project-expand-btn mr-2"
                      :disabled="!rowData.employees || rowData.employees.length < 2"
                      @click="toggleProjectExpansion(rowData.projectId)">
                      <span class="material-symbols-outlined" style="font-size: 20px">
                        {{ expandedProjects[rowData.projectId] ?  'keyboard_arrow_down' : 'keyboard_arrow_right'}}
                      </span>
                    </button>
                    <ProjectInitialsButton
                      :project-name="rowData.projectName"
                      :project-color="rowData.projectColor"
                      :project-data="rowData"
                      @click="getSlideOut"
                    />
                    <TableProjectItem hideNumber :link="rowData.projectKey" :project-name="rowData.projectName"
                      :project-subtitle="rowData.projectSubtitle ? rowData.projectSubtitle : ''"
                      :project-clients="rowData.clientName" view="table-row" />
                  </div>
                </b-td>
                <b-td>
                  {{ rowData.salesChannel }}
                </b-td>
                <b-td>
                  <div v-if="rowData.preSalesManager" class="d-flex align-items-center"
                       :id="`presales-manager-${rowData.originalIndex}`">
                    <b-img
                      v-if="rowData.preSalesManager.avatar_url"
                      :src="rowData.preSalesManager.avatar_url"
                      rounded="circle"
                      :alt="`${rowData.preSalesManager.name} profile image`"
                      class="user-profile-avatar mr-2"
                    />
                    <b-img
                      v-else
                      src="/images/avatar-placeholder.png"
                      rounded="circle"
                      alt="Failed to load profile image"
                      class="user-profile-avatar mr-2"
                    />
                    <div>
                      <div class="presales-name">{{ rowData.preSalesManager.name }}</div>
                      <div class="presales-position">{{ rowData.preSalesManager.position }}</div>
                    </div>
                  </div>
                  <div v-else>-</div>
                   <!-- Tooltips -->
                  <b-tooltip v-if="rowData.preSalesManager"
                    :key="`presales-tooltip-${rowData.originalIndex}`"
                    :target="`presales-manager-${rowData.originalIndex}`"
                    variant="base" container="body" placement="top" boundary="viewport"
                    html>
                    <div class="text-left">
                      {{ rowData.preSalesManager.name }}
                      <br v-if="rowData.preSalesManager.position" />
                      {{ rowData.preSalesManager.position }}
                      <br v-if="rowData.preSalesManager.employment_type && rowData.preSalesManager.payment_type" />
                      <span v-if="rowData.preSalesManager.employment_type && rowData.preSalesManager.payment_type">
                        {{ (rowData.preSalesManager.employment_type.label || '') + ' / ' + (rowData.preSalesManager.payment_type || '') }}
                      </span>
                    </div>
                  </b-tooltip>
                </b-td>
                <b-td v-if="$can(permissions.ReadAllComments)">
                  <div class="position-relative pr-1">
                    <TextEditor class="text-left" show-text data-already-loaded empty-text="no comment"
                      :entity-type="commentEntityTypes.SalesReportProject" :entity-id="rowData.projectId" />
                  </div>
                </b-td>
                <b-td>
                  <div class="d-flex">
                    <div v-for="(employee, index) in getEmployeesList(rowData.employees)" :key="employee.name"
                      class="employee-wrap" :style="{
                        zIndex: rowData.employees.slice(0, maxEmployeeNumber).length - index,
                        marginLeft: index !== 0 ? '-8px' : 'unset'
                      }">
                      <img v-if="employee.avatar_url && employee.name !== 'By project' && employee.name !== 'Extra payments'"
                        v-b-tooltip.hover.html="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }"
                        :title="getEmployeeTooltip(employee)"
                        :src="employee.avatar_url"
                        class="employee-avatar"
                        :alt="employee.name" />
                      <span v-else-if="employee.name === 'By project' || employee.name === 'Extra payments'"
                        v-b-tooltip.hover.html="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }"
                        :title="getEmployeeTooltip(employee)"
                        class="material-symbols-outlined"
                        style="color: #3D5170; font-size: 16px; padding-left: 1px;">
                        attach_money
                      </span>
                      <span v-else
                        v-b-tooltip.hover.html="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }"
                        :title="getEmployeeTooltip(employee)"
                        class="material-icons">
                        person
                      </span>
                    </div>
                    <div v-if="isEmployeesMoreThanMaxNumber(rowData.employees)">
                      <b-button :id="`${data.managerName}-${rowData.originalIndex}-${rowData.projectName}`" variant="link"
                        class="employee-wrap more" :style="{
                          zIndex: 1,
                          marginLeft: '-8px',
                          backgroundColor: '$bgColorBase !important'
                        }">
                        +{{ getResEmployeesNumber(rowData.employees) }}
                      </b-button>
                      <b-popover placement="bottom" custom-class="table-sales-report-item-popover"
                        :target="`${data.managerName}-${rowData.originalIndex}-${rowData.projectName}`" triggers="focus">
                        <b-list-group>
                          <div v-for="employee in rowData.employees.slice(maxEmployeeNumber - 1)" :key="employee.name"
                            style="padding: 8px 16px; display: flex; align-items: center; gap: 12px;">
                            <div class="employee-wrap">
                              <span v-if="employee.name === 'By project' || employee.name === 'Extra payments'"
                                class="material-symbols-outlined">
                                attach_money
                              </span>
                              <img v-else-if="employee.avatar_url"
                                :src="employee.avatar_url"
                                class="employee-avatar"
                                :alt="employee.name" />
                              <span v-else class="material-icons">
                                person
                              </span>
                            </div>
                            <span v-if="employee.name === 'By project' || employee.name === 'Extra payments'">{{ employee.name }}</span>
                            <span v-else>{{ `${employee.name} (${employee.startDate})` }}</span>
                          </div>
                        </b-list-group>
                      </b-popover>
                    </div>
                  </div>
                </b-td>
                <b-td>
                  <div class="d-flex justify-content-end">
                    {{ rowData.plannedAmount }}
                  </div>
                </b-td>
                <b-td>
                  <div class="d-flex justify-content-end">
                    <div v-b-tooltip.hover.html="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title="
                      getPaidByTooltipHtmlWithName(rowData.projectName, rowData.paidAmount, rowData.plannedAmount)
                    " :class="getPaidAmountClass(rowData)">
                      <span :class="{ 'employee-paid-amount': expandedProjects[rowData.projectId] }">
                        {{ rowData.paidAmount }}
                      </span>
                    </div>
                  </div>
                </b-td>
                <b-td>
                  {{ rowData.commission }}
                </b-td>
                <b-td>
                  <b-button :id="`invoices-${rowData.originalIndex}-${rowData.projectName}`"
                    @click.prevent="() => onOpen(`popover-invoices-${rowData.originalIndex}`)" variant="link"
                    class="employee-wrap more invoices" :style="{
                      width: '32px', height: '32px',
                      backgroundColor: '#E4EFFF', fontSize: '13px', color: '#3D5170'
                    }">
                    {{ rowData.invoices.length }}
                  </b-button>
                  <InvoicePopup
                    :title="`Invoices for ${data.managerName} - ${rowData.projectName}`"
                    :invoices="rowData.invoices"
                    :target="`invoices-${rowData.originalIndex}-${rowData.projectName}`"
                    :popover-ref="`popover-invoices-${rowData.originalIndex}`"
                    custom-class="table-sales-report-invoices-popover"
                    :show.sync="show[`invoices-${rowData.originalIndex}-${rowData.projectName}`]"
                    @close="onClose(`popover-invoices-${rowData.originalIndex}`)"
                    @reloadInvoices="$emit('reloadInvoices'); onClose(`popover-invoices-${rowData.originalIndex}`)"
                    @showEditForm="(invoice) => showToTheTopAndEdit(invoice, rowData.originalIndex)"
                  />
                </b-td>
              </template>

              <!-- Employee Detail Row Content -->
              <template v-else>
                <b-td></b-td> <!-- Empty project column -->
                <b-td></b-td> <!-- Empty sales channel column -->
                <b-td></b-td> <!-- Empty pre-sales column -->
                <b-td v-if="$can(permissions.ReadAllComments)"></b-td> <!-- Empty comment column -->
                <b-td>
                  <!-- Show avatar + date only if not Extra payments / By project -->
                  <template v-if="rowData.name !== 'Extra payments' && rowData.name !== 'By project'">
                      <div class="d-flex align-items-center"  v-b-tooltip.hover.html="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }"
                      :title="getEmployeeTooltip(rowData)">
                      <b-img
                        v-if="rowData.avatar_url"
                        :src="rowData.avatar_url"
                        rounded="circle"
                        :alt="`${rowData.name} profile image`"
                        class="user-profile-avatar mr-2"
                      />
                      <b-img
                        v-else
                        src="/images/avatar-placeholder.png"
                        rounded="circle"
                        alt="Failed to load profile image"
                        class="user-profile-avatar mr-2"
                      />
                      <div>
                        <div class="employee-detail-name">{{ rowData.name }}</div>
                        <div class="employee-detail-date">{{ rowData.startDate }}</div>
                      </div>
                    </div>
                  </template>

                  <!-- For Extra payments / By project only show name -->
                  <template v-else>
                    <div class="employee-detail-name" style="padding: 6px; line-height: 24px;">{{ rowData.name }}</div>
                  </template>
                </b-td>

                <b-td>
                  <div class="d-flex justify-content-end">
                    {{ rowData.plannedAmount }}
                  </div>
                </b-td>
                <b-td>
                  <div class="d-flex justify-content-end">
                    <div v-b-tooltip.hover.html="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :class="getEmployeePaidAmountClass(rowData)" :title="
                        getPaidByTooltipHtmlWithName(rowData.name, rowData.paidAmount, rowData.plannedAmount)
                      ">
                      <span>
                        {{ rowData.paidAmount }}
                      </span>
                    </div>
                  </div>
                </b-td>
                <b-td></b-td> <!-- Empty commission column -->
                <b-td></b-td> <!-- Empty invoices column -->
              </template>
            </b-tr>
            <!-- Totals Footer Row -->
            <b-tr class="totals-footer" :class="{ 'no-bottom-border': isCollapsed && showSalesCommission }">
              <!-- When card is open - show toggle switch -->
              <template v-if="isCollapsed">
                <b-td class="commission-toggle-formula text-left" :colspan="getFirstFiveColumnsSpan()">
                  <div class="commission-toggle-wrapper">
                    <FormGeneratorItems
                      :field="salesCommissionSwitchField"
                      :value="showSalesCommission"
                      @input="showSalesCommission = $event"
                    />
                  </div>
                </b-td>
                <b-td class="text-right">
                  <strong>TOTAL</strong>
                </b-td> <!-- Employee column shows TOTAL -->
                <b-td>
                  <strong>{{ getTotalPlanned() }}</strong>
                </b-td> <!-- Planned $ total -->
                <b-td>
                  <div class="d-flex justify-content-end">
                    <div v-b-tooltip.hover.html="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title="getTotalPaidByTooltipHtml()" class="manager-totals-amount" :class="getTotalPaidAmountClass()">
                      <strong>{{ getTotalPaid() }}</strong>
                    </div>
                  </div>
                </b-td> <!-- Paid $ total -->
                <b-td></b-td> <!-- Commission column -->
                <b-td></b-td> <!-- Invoices column -->
              </template>

              <!-- When Sales Commission is hidden -->
              <template v-else>
                <b-td></b-td> <!-- Project column -->
                <b-td></b-td> <!-- Channel column -->
                <b-td></b-td> <!-- Pre sales column -->
                <b-td v-if="$can(permissions.ReadAllComments)"></b-td> <!-- Comment column -->
                <b-td class="text-right">
                  <strong>TOTAL</strong>
                </b-td> <!-- Employee column shows TOTAL -->
                <b-td>
                  <strong>{{ getTotalPlanned() }}</strong>
                </b-td> <!-- Planned $ total -->
                <b-td>
                  <div class="d-flex justify-content-end">
                    <div v-b-tooltip.hover.html="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title="getTotalPaidByTooltipHtml()" class="manager-totals-amount"  :class="getTotalPaidAmountClass()">
                      <strong>{{ getTotalPaid() }}</strong>
                    </div>
                  </div>
                </b-td> <!-- Paid $ total -->
                <b-td></b-td> <!-- Commission column -->
                <b-td></b-td> <!-- Invoices column -->
              </template>
            </b-tr>

            <!-- Commission Information Row (shows when toggle is ON) -->
            <b-tr v-if="isCollapsed && showSalesCommission" class="commission-info-row">
              <b-td class="sales-commission-formula text-left" :colspan="getTotalColumnsCount()">
                  <span v-if="data.salesCommissionFormula" v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title="data.salesCommissionFormulaInfo">
                    {{ data.managerName }}, $: <br/> {{ data.salesCommissionFormula }}
                  </span>
                  <span v-else>
                    {{ data.managerName }}, $: 0
                  </span>
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </div>
    </b-collapse>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import { permissions } from '@mocks/permissions'
import TableProjectItem from '@components/tables/TableProjectItem'
import { commentEntityTypes } from '@mocks/comment-entity-types'
import TextEditor from '@components/TextEditor'
import InvoiceFormModal from '@mixins/InvoiceFormModal'
import ScrollPositionMixin from '@mixins/ScrollPositionMixin'
import InvoicePopup from '@components/InvoicePopup'
import ProjectInitialsButton from '@components/ProjectInitialsButton'
import FormGeneratorItems from '@components/FormGeneratorItems'

export default {
  name: 'TableSalesReportItem',
  components: {
    TextEditor,
    TableProjectItem,
    InvoicePopup,
    ProjectInitialsButton,
    FormGeneratorItems
  },
  mixins: [InvoiceFormModal, ScrollPositionMixin],
  props: {
    data: Object,
    currentFields: Array,
    isShowAllFields: Boolean,
    preSalesCommissionFormulas: Array
  },
  data () {
    return {
      permissions,
      isCollapsed: false,
      maxEmployeeNumber: 5,
      showSlideOut: false,
      openProject: null,
      show: {},
      expandedProjects: {}, // Track which projects have expanded employee rows
      showSalesCommission: false // Track whether to show sales commission for this card
    }
  },
  computed: {
    commentEntityTypes () {
      return commentEntityTypes
    },
    flattenedProjects () {
      const result = []

      this.data.projects.forEach((project, index) => {
        // Add the main project row
        result.push({
          ...project,
          isEmployeeRow: false,
          originalIndex: index
        })

        // Add employee detail rows if there's more than 1 employee and project is expanded
        if (project.employees && project.employees.length > 1 && this.expandedProjects[project.projectId]) {
          project.employees.forEach(employee => {
            result.push({
              ...employee,
              isEmployeeRow: true,
              parentProject: project,
              originalIndex: index
            })
          })
        }
      })

      return result
    },
    expandableProjects () {
      // Projects that have more than 1 employee and can be expanded
      return this.data.projects.filter(project => project.employees && project.employees.length > 1)
    },
    salesCommissionSwitchField () {
      // Configuration for the sales commission switch using FormGeneratorItems
      return {
        type: 'switch',
        name: `sales-commission-toggle-${this.data.managerId}`,
        label: this.showSalesCommission ? 'Hide sales commission' : 'Show sales commission'
      }
    }
  },
  methods: {
    toggleCollapse () {
      this.isCollapsed = !this.isCollapsed
      this.$emit('stateChanged')
    },
    getEmployeeTooltip (employee) {
      const userName = employee.name || employee.userName
      if (userName === 'Extra payments' || userName === 'By project') {
        return `<div class="text-left">${userName}</div>`
      }
      const userPosition = employee.position || employee.userPosition
      const startDate = employee.startDate ? `Start: ${employee.startDate}` : ''
      let extraInfo = ''

      if (employee.employment_type && employee.payment_type) {
        extraInfo = `${employee.employment_type.label || ''} / ${employee.payment_type || ''}`
      }

      const tooltipParts = [userName]
      if (userPosition) tooltipParts.push(userPosition)
      if (startDate) tooltipParts.push(startDate)
      if (extraInfo) tooltipParts.push(extraInfo)

      return `<div class="text-left">${tooltipParts.join(' <br/> ')}</div>`
    },
    getField (name) {
      const currentTableFields = cloneDeep(this.currentFields)
      return currentTableFields.find((item) => item.key === name)
    },
    getEmployeeRowVariant (project, employee) {
      if (employee.paidAmount === 0 || project.preSalesManager.length === 0) {
        return 'light'
      } else if (employee.loggedHours < 168) {
        return 'primary'
      } else if (employee.paidAmount >= 1 && employee.loggedHours >= 168) {
        return 'secondary'
      }
    },
    getPaidByTooltipHtmlWithName (name, paidAmount, plannedAmount) {
      const notPaid = this.roundValue(plannedAmount - paidAmount)
      return `<div class="text-left">
        <strong>${name}</strong><br>
        Paid: ${paidAmount}<br>
        Not paid: ${notPaid}
      </div>`
    },
    getTotalPaidByTooltipHtml () {
      const totalPaid = this.getTotalPaid()
      const totalPlanned = this.getTotalPlanned()
      const notPaid = this.roundValue(totalPlanned - totalPaid)
      return `<div class="text-left">
        <strong>Total</strong><br>
        Paid: ${totalPaid}<br>
        Not paid: ${notPaid}
      </div>`
    },
    roundValue (value) {
      return Math.round(value * 100) / 100
    },
    summaryPlannedTotal () {
      return this.roundValue(
        this.data.itemsByManager.reduce(
          (sum, managerData) => sum + managerData.plannedTotal,
          0
        )
      )
    },
    summaryPaidTotal () {
      return this.roundValue(
        this.data.itemsByManager.reduce(
          (sum, managerData) => sum + managerData.paidTotal,
          0
        )
      )
    },
    getEmployeesList (employees) {
      const numberToShow = this.isEmployeesMoreThanMaxNumber(employees)
        ? this.maxEmployeeNumber - 1
        : employees.length

      return employees.slice(0, numberToShow)
    },
    isEmployeesMoreThanMaxNumber (employees) {
      return employees.length >= this.maxEmployeeNumber + 1
    },
    getResEmployeesNumber (employees) {
      return employees.length - (this.maxEmployeeNumber - 1)
    },
    getSlideOut (project) {
      this.$emit('getSlideOut', project)
    },
    onOpen (key) {
      const popoverRef = key
      const popover = this.$refs[popoverRef]
      if (popover && popover[0]) {
        popover[0].$emit('open')
      }
    },
    onClose (refKey) {
      const popover = this.$refs[refKey]
      if (popover && popover[0]) {
        popover[0].$emit('close')
      }
    },
    getPaidAmountClass (projectData) {
      if (projectData.paidAmount < projectData.plannedAmount) {
        return 'paid-amount-partial'
      } else if (projectData.paidAmount >= projectData.plannedAmount) {
        return 'paid-amount-complete'
      }
      return ''
    },
    getEmployeePaidAmountClass (employee) {
      if (employee.paidAmount < employee.plannedAmount) {
        return 'paid-amount-partial'
      } else if (employee.paidAmount >= employee.plannedAmount) {
        return 'paid-amount-complete'
      }
      return ''
    },
    getTotalPlanned () {
      return this.roundValue(
        this.data.projects.reduce((sum, project) => sum + project.plannedAmount, 0)
      )
    },
    getTotalPaid () {
      return this.roundValue(
        this.data.projects.reduce((sum, project) => sum + project.paidAmount, 0)
      )
    },
    getTotalPaidAmountClass () {
      const totalPaid = this.data.projects.reduce((sum, project) => sum + project.paidAmount, 0)
      const totalPlanned = this.data.projects.reduce((sum, project) => sum + project.plannedAmount, 0)

      if (totalPaid < totalPlanned) {
        return 'paid-amount-partial'
      } else if (totalPaid >= totalPlanned) {
        return 'paid-amount-complete'
      }
      return ''
    },
    getFirstFiveColumnsSpan () {
      // Calculate span for first 3 columns: Project, Channel, Pre sales
      let span = 3

      // Add comment column if visible
      if (this.$can(this.permissions.ReadAllComments)) {
        span += 1
      }

      return span
    },
    showToTheTopAndEdit (invoice, index) {
      // Close the popover first
      this.onClose(`popover-invoices-${index}`)
      // Scroll to top and emit edit form event
      this.scrollToTheTop()
      this.$emit('showEditForm', invoice)
    },
    toggleProjectExpansion (projectId) {
      if (this.expandedProjects[projectId]) {
        this.$delete(this.expandedProjects, projectId)
      } else {
        this.$set(this.expandedProjects, projectId, true)
      }
      this.$emit('stateChanged')
    },
    expandAllProjects () {
      // Method to be called by parent when ALL FIELDS is clicked
      this.expandableProjects.forEach(project => {
        this.$set(this.expandedProjects, project.projectId, true)
      })
    },
    collapseAllProjects () {
      // Method to be called by parent when HIDE FIELDS is clicked
      this.expandedProjects = {}
    },
    expandManagerCard () {
      // Method to be called by parent when ALL FIELDS is clicked
      this.isCollapsed = true
    },
    collapseManagerCard () {
      // Method to be called by parent when HIDE FIELDS is clicked
      this.isCollapsed = false
    },
    closeAllPopups () {
      // Method to close all invoice popups
      this.show = {}
    },
    getCommissionToggleColspan () {
      // Calculate colspan for commission toggle (Employee + Planned $ + Paid $ + Commission + Invoices columns)
      return 5
    },
    getTotalColumnsCount () {
      // Calculate total number of columns for commission info row
      let count = 7 // Base columns: Project, Channel, Pre sales, Employee, Planned $, Paid $, Commission, Invoices
      if (this.$can(this.permissions.ReadAllComments)) {
        count += 1 // Add comment column if visible
      }
      return count
    }
  }
}
</script>

<style lang="scss" scoped>
.wrap {
  background-color: $bgColorWhite;
  // padding: 12px 15px;
  border-radius: 10px;
}

.wrap_card {
  min-width: calc(#{$sales-table-min-width} + 30px);
}

.manager-name {
  font-family: Ubuntu;
  font-size: 13px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  margin: unset;
}

.divider {
  border-bottom: 1px solid $bgColor2;
}

.table-wrapper {
  padding: 12px 15px;
}

.table__theme-default table td {
  padding-right: 8px;
  padding-top: 14px;
  padding-bottom: 14px;
}

 ::v-deep .project-subtitle, ::v-deep .project-title {
    margin-bottom: 0;
  }

  ::v-deep .project-title {
    line-height: 22px;
  }

  ::v-deep  .wrap.project-item {
    flex: 1 1 auto;          /* allow it to shrink */
    min-width: 0;            /* CRUCIAL in flexbox for ellipsis */
    overflow: hidden;        /* needed for ellipsis */
    text-overflow: ellipsis;
    white-space: nowrap;     /* keep single line */
}

  ::v-deep .project-item {
    padding: 0 8px !important;
}

  .table__theme-default table td {
    padding-top: 7px;
    padding-bottom: 7px;
}

.view-table-row {
  display: flex;
  align-items: center;

  .project-owner {
    display: none;
  }
}

.employee-wrap {
  background-color: $bgColorBase;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $iconColor2;
  border: 2px solid #fff;
  position: relative;
}

.employee-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
}

.employee-wrap.more {
  text-decoration: none;
  background-color: $bgColorBase;
  color: $buttonTxtColor2;
  font-size: 10px;
}

.employee-wrap.invoices {
  border-radius: 5px;
  border: none;
}

.employee-wrap.more:focus {
  color: #0063F8 !important;
  box-shadow: unset;
  background-color: #C3DBFF;
  border-color: #0063F8;
}

.table td {
  vertical-align: middle;
}

.table-responsive {
  margin-bottom: 0 !important;
}

.table__theme-default {
  width: 100% !important;
  table-layout: fixed !important;
  min-width: $sales-table-min-width !important;

  th, td {
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
  }

  // Base column widths (always present) - using SCSS variables
  th:nth-child(1), td:nth-child(1) { width: $sales-col-projects !important; max-width: $sales-col-projects !important; } // Projects (240px)
  th:nth-child(2), td:nth-child(2) { width: $sales-col-channel !important; max-width: $sales-col-channel !important; } // Channel (72px)
  th:nth-child(3), td:nth-child(3) { width: $sales-col-presales !important; max-width: $sales-col-presales !important; } // Pre sales (168px)
  th:nth-child(5), td:nth-child(5) { width: $sales-col-employee !important; max-width: $sales-col-employee !important; } // Employee (200px)

  // Last columns (always present) - using nth-last-child to count from end
  th:nth-last-child(4), td:nth-last-child(4) { width: $sales-col-planned !important; max-width: $sales-col-planned !important; } // Planned $ (96px)
  th:nth-last-child(3), td:nth-last-child(3) { width: $sales-col-paid !important; max-width: $sales-col-paid !important; } // Paid $ (96px)
  th:nth-last-child(2), td:nth-last-child(2) { width: $sales-col-commission !important; max-width: $sales-col-commission !important; } // Commission (96px)
  th:nth-last-child(1), td:nth-last-child(1) { width: $sales-col-invoices !important; max-width: $sales-col-invoices !important; } // Invoices (96px)

  // Comment column gets extra width (appears as 5th column when present)
  th:nth-child(4), td:nth-child(4) { width: $sales-col-comment !important; max-width: $sales-col-comment !important; } // Comment column (200px)

  // Remove left padding from first column only
  th:nth-child(1), td:nth-child(1) {
    padding-left: 0 !important;
  }

  // Right align monetary columns
  th:nth-last-child(4), td:nth-last-child(4), // Planned $
  th:nth-last-child(3), td:nth-last-child(3), // Paid $
  th:nth-last-child(2), td:nth-last-child(2)  // Commission
  {
    text-align: right !important;
  }

}

// Base class for all amount indicators with common styling
.amount-indicator {
  padding: 0 4px;
  border-radius: 3px;
  line-height: 20px;
}

.paid-amount-partial {
  @extend .amount-indicator;
  background-color: #FFDFC5;
}

.paid-amount-complete {
  @extend .amount-indicator;
  background-color: #C3E6CB;
}

.presales-name {
  font-size: 13px;
  line-height: 20px;
  color: $tableTextHeadColor2;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 130px;
  white-space: nowrap;
}

.presales-position {
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  color: $tableTextHeadColor;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 100px;
  white-space: nowrap;
}

.mr-2 {
  margin-right: 0.5rem;
}

.totals-footer {
  border-top: 1px solid #dee2e6 !important;
  font-weight: bold;

  td {
    border-top: 1px solid #dee2e6 !important;
    padding-top: 15px !important;
    padding-bottom: 0 !important;
  }
}

.manager-totals-grid {
  display: grid;
  align-items: center;
  width: 100%;
  padding: 12px 15px;

  // Grid layout for single row with manager name, totals, and arrow
  &.with-comments {
    grid-template-columns: 1fr $sales-col-employee $sales-col-planned $sales-col-paid $sales-col-commission $sales-col-invoices; // Manager name spans first columns, then Employee, Planned, Paid, Commission, Invoices/Arrow
  }

  &.without-comments {
    grid-template-columns: 1fr $sales-col-employee $sales-col-planned $sales-col-paid $sales-col-invoices; // Manager name spans first columns, then Employee, Planned, Paid, Invoices/Arrow
  }
}

.manager-name-cell {
  // Manager name spans multiple columns and takes remaining space
  grid-column: 1;
}

.arrow-icon {
  flex-shrink: 0;
}

.total-arrow {
  display: flex;
  justify-content: end;
  align-items: center;
}

.total-cell {
  display: flex;
  align-items: center;

  &.total-employee {
    justify-content: flex-end;
    text-align: right;
  }

  &.total-planned, &.total-paid {
    justify-content: flex-end;
    text-align: right;
  }

  &.total-planned {
    padding-right: 4px;
  }

  &.total-paid {
    padding-right: 8px;
  }

  &.manager-name-cell {
    justify-content: flex-start;
    text-align: left;
  }
}

.manager-totals-label {
  font-family: Ubuntu;
  padding-right: 8px;
  font-size: 13px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  text-transform: uppercase;
}

.manager-totals-amount {
  @extend .amount-indicator;
  font-family: Ubuntu;
  font-size: 13px;
  font-style: normal;
  font-weight: 700;
}

.employee-detail-row {

  td {
    padding-top: 7px !important;
    padding-bottom: 7px !important;
    border-top: none !important;
  }
}

.employee-paid-amount {
  font-weight: 700;
}

.employee-detail-name {
  font-size: 13px;
  line-height: 20px;
  color: $tableTextHeadColor2;
  font-weight: 400;
}

.employee-detail-date {
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  color: $tableTextHeadColor;
}

.project-expand-btn {
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  span {
    display: block;
  }
}

.commission-toggle-formula {
  padding: 8px 12px !important;
}

// Override the table theme default border for totals footer with no bottom border
.table__theme-default table tbody tr.totals-footer.no-bottom-border {
  border-bottom: none !important;
}

.commission-toggle-wrapper {
  margin-left: -12px;
  ::v-deep .col {
    padding: 0;
    margin: 0;
    max-width: none;
    flex: none;

    .switch-form-group {
      margin: 0;

      > div {
        display: flex !important;
        flex-direction: row !important;
        justify-content: flex-start !important;
        align-items: center;

        button {
          background: transparent !important;
          border: none;
          cursor: auto !important;
          padding: 0;
          margin: 0;
          order: 1;
          position: relative;
          left: 0;
        }

        label {
          font-size: 13px;
          font-weight: 500;
          color: $fontColorBase;
          margin: 0 0 0 12px !important;
          order: 2;
          white-space: nowrap;
        }
      }
    }
  }
}

.commission-info-row {
  td {
    padding-top: 8px !important;
    padding-bottom: 0 !important;
    border-top: none !important;
  }
  .sales-commission-formula {
    padding-top: 0 !important;
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
  }
}

.commission-info-content {
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  padding: 8px 12px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #007bff;

  .font-weight-bold {
    margin-right: 8px;
  }
}
</style>
